import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Desktop } from '../components/Desktop';
import { BootScreen } from '../components/BootScreen';
import { WelcomeScreen } from '../components/WelcomeScreen';
import OSWhiteScreenBoundary from '../components/OSWhiteScreenBoundary';
import { useAuth as useMainAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '../hooks/use-mobile';
import { useAuth } from '../hooks/useAuth';
import { useOsLicense } from '@/hooks/supabase/useOsLicense';
import { useLicenseActivations } from '@/hooks/supabase/useLicenseActivations';

const Index = () => {
  const { user: osUser, loading: osLoading } = useAuth();
  const { user: mainUser, loading: mainLoading } = useMainAuth();
  const { hasProOrEnterprise } = useOsLicense();
  const location = useLocation();

  const user = mainUser ?? osUser;
  const loading = osLoading || mainLoading;
  
  const isMobile = useIsMobile();
  
  const [showWelcome, setShowWelcome] = useState(false);
  
  const [bootState, setBootState] = useState(() => {
    const lastBootTime = localStorage.getItem('MapleOS-last-boot');
    const hasBooted = localStorage.getItem('MapleOS-booted') === 'true';
    
    console.log('Index: Boot state initialization - hasBooted:', hasBooted, 'lastBootTime:', lastBootTime);
    
    if (hasBooted && lastBootTime) {
      const timeSinceLastBoot = Date.now() - parseInt(lastBootTime);
      const hoursAway = timeSinceLastBoot / (1000 * 60 * 60);
      
      if (hoursAway > 4) {
        console.log('User away for', hoursAway.toFixed(1), 'hours - triggering boot screen');
        localStorage.removeItem('MapleOS-booted');
        localStorage.removeItem('MapleOS-last-boot');
        return 'not-started';
      }
      console.log('Index: Boot already completed, setting to completed');
      return 'completed';
    }
    console.log('Index: No boot found, setting to not-started');
    return 'not-started';
  });

  useLicenseActivations({ autoActivate: Boolean(user && hasProOrEnterprise && bootState === 'completed') });
  
  console.log('Index: Render - user:', user?.email, 'loading:', loading, 'bootState:', bootState, 'ALL CONDITIONS:', {
    isLoading: loading,
    hasUser: !!user,
    bootCompleted: bootState === 'completed',
    shouldShowLogin: !user && bootState === 'completed',
    shouldShowDesktop: user && bootState === 'completed'
  });
  
  useEffect(() => {
    console.log('Index: useEffect triggered - bootState:', bootState);
    
    if (bootState === 'not-started') {
      console.log('Index: Starting boot sequence (ONCE)');
      setBootState('booting');
    }
  }, []);
  
  const handleBootComplete = () => {
    console.log('Index: Boot completed - storing timestamp');
    localStorage.setItem('MapleOS-booted', 'true');
    localStorage.setItem('MapleOS-last-boot', Date.now().toString());
    setBootState('completed');
    
    const hasSeenWelcome = localStorage.getItem('MapleOS-welcome-seen');
    const sessionDismissed = sessionStorage.getItem('MapleOS-welcome-session-dismissed');
    if (!hasSeenWelcome && user && !sessionDismissed) {
      setTimeout(() => {
        setShowWelcome(true);
      }, 1000);
    }
  };

  const handleWelcomeClose = () => {
    sessionStorage.setItem('MapleOS-welcome-session-dismissed', 'true');
    setShowWelcome(false);
  };

  const handleDontShowWelcomeAgain = (dontShow: boolean) => {
    if (dontShow) {
      localStorage.setItem('MapleOS-welcome-seen', 'true');
    }
  };
  
  useEffect(() => {
    if (user && bootState === 'completed') {
      const hasSeenWelcome = localStorage.getItem('MapleOS-welcome-seen');
      const sessionDismissed = sessionStorage.getItem('MapleOS-welcome-session-dismissed');
      if (!hasSeenWelcome && !sessionDismissed) {
        setTimeout(() => {
          setShowWelcome(true);
        }, 1000);
      }
    }
  }, [user, bootState]);

  useEffect(() => {
    if (!user) {
      sessionStorage.removeItem('MapleOS-welcome-session-dismissed');
    }
  }, [user]);
  
  if (bootState === 'booting') {
    console.log('Index: Showing BootScreen');
    return <BootScreen isVisible={true} onComplete={handleBootComplete} />;
  }
  
  if (loading) {
    console.log('Index: Showing loading screen');
    return <div className="fixed inset-0 bg-black flex items-center justify-center">
      <div className="text-white">Loading...</div>
    </div>;
  }
  
  if (!user && bootState === 'completed') {
    console.log('Index: Redirecting unauthenticated user to shared auth screen');
    return (
      <Navigate
        to="/auth"
        replace
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }
  
  if (user && bootState === 'completed') {
    console.log('Index: ✅ CONDITION MET - Showing Desktop');
    return (
      <OSWhiteScreenBoundary>
        <Desktop shellOverlayActive={!isMobile && showWelcome} />
        {!isMobile && (
          <WelcomeScreen
            isVisible={showWelcome}
            onClose={handleWelcomeClose}
            onDontShowAgain={handleDontShowWelcomeAgain}
          />
        )}
      </OSWhiteScreenBoundary>
    );
  }
  
  console.log('Index: ❌ NO CONDITIONS MET - Showing fallback. Waiting for:', {
    needsUser: !user ? 'user to be null (✓)' : 'user to exist (current: ' + user?.email + ')',
    needsBoot: bootState !== 'completed' ? 'boot to complete (current: ' + bootState + ')' : 'boot completed (✓)',
    needsLoading: loading ? 'loading to finish' : 'loading finished (✓)'
  });
  return <div className="fixed inset-0 bg-black flex items-center justify-center">
    <div className="text-white">Waiting for conditions...</div>
  </div>;
};

export default Index;